import {computed, Injectable, Signal, signal} from '@angular/core';
import {AgendaDokumentValgSpesifikasjon, AkseptertSignaturmetode, ArkivnodeId, EnkelGruppeModell, ForfalteOppgaverDokumentValgSpesifikasjon, GruppeId, GruppeModell, GruppeRessursClient, NyAvtaleOgGruppeSpesifikasjon, NyGruppeSpesifikasjon, OppdaterGruppeSpesifikasjon, ProtokollValgSpesifikasjon, SlettGruppeResponsModell, SlettGruppeSpesifikasjon} from "../ressurser/generertNg2HttpKlient";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FeilhåndteringArgumenter, hentFraLokallagerEllerNull, lesId, opprettEllerOppdatertHendelse, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettHendelse, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {DataOgMeta, kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {firstValueFrom, Observable} from "rxjs";
import {tap} from "rxjs/operators";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {createStore, filterNil} from "@ngneat/elf";
import {deleteEntities, selectAllEntities, selectEntity, setEntities, updateEntities, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {HttpResponse} from "@angular/common/http";
import {sorterGrupperPåOrganisasjonSåTittel} from "felles-ng2/modell/gruppeModellHjelpere";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";

const debug = initDebugger('GruppeDatatjeneste');

@Injectable() //tilgjengeliggjøres via DatalagerModul
export class GruppeDatatjeneste {

    private _aktivGruppe = signal<GruppeModell | null>(null);

    /** @deprecated vil vil ikke bruke denne lenger - det er veldig vanskelig å forholde seg til om denne er satt eller ikke.*/
    public readonly aktivGruppeIkkeNull = computed<GruppeModell>(() => {
        const gruppe = this._aktivGruppe()
        if (erNil(gruppe)) throw new Error('GruppeDatatjeneste.aktivGruppe er null, har du glemt å kalle GruppeDatatjeneste.aktivGruppe = <en GruppeModell>?')
        return gruppe!;
    });

    /** Lager for {@link GruppeModell}.
     * Uten forelder, da vi ikke gjør oppslag mot noen logisk forelder */
    private gruppeLager = createStore(
      {name: 'gruppe'},
      withEntities<GruppeModell>());

    constructor(private gruppeRessurs: GruppeRessursClient,
                private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
        this.propagerEndringerTilAndreDatatjenester();
    }

    get aktivGruppe(): Signal<GruppeModell | null> {
        return this._aktivGruppe;
    }

    set aktivGruppe(gruppe: GruppeModell) {
        this._aktivGruppe.set(gruppe);
    }

    /** Returnerer gruppe registrert i lokallageret.
     * Forutsetter at feks {@link #erstattILokallager} har blitt kalt i forkant */
    hentFraLokallagerSomObserverbar(id: GruppeId): Observable<GruppeModellDML> {
        return this.gruppeLager.pipe(selectEntity(id), filterNil());
    }

    /** Returnerer gruppe fra lokallager.
     * Returnerer null dersom gruppe ikke er registrert i lokallager. */
    hentFraLokallagerEllerNull(id: ArkivnodeId): GruppeModellDML | null {
        return hentFraLokallagerEllerNull(this.gruppeLager, id);
    }

    erstattILokallager(gruppe: GruppeModell) {
        this.gruppeLager.update(upsertEntities(gruppe));
    }

    hentMedMeta(id: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeModellDML>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.hent(id),
            registrerILager: (respons: HttpResponse<GruppeModell>) => {
                this.gruppeLager.update(upsertEntities(respons.body!));
                return respons.body!
            },
            hentFraLager: () => this.gruppeLager.pipe(selectEntity(id), filterNil()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente gruppe', 'hentMedMeta')
        });
    }

    hent(id: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<GruppeModellDML> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    tilgjengeligeGrupperMedMeta(feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.finnTilgjengeligeGrupper(),
            registrerILager: (respons: HttpResponse<GruppeModell[]>) => {
                const grupper = respons.body!
                this.gruppeLager.update(setEntities(grupper)); //erstatter alle i lokallagret med mottatt samling
                return grupper;
            },
            hentFraLager: () => this.gruppeLager.pipe(selectAllEntities(), sorterLevertSamling<GruppeModell>(standardSortering)),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente grupper")), 'tilgjengeligeGrupper')
        });
    }

    tilgjengeligeGrupper(feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<GruppeModell[]> {
        return this.tilgjengeligeGrupperMedMeta(somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Sørger for å hente ferske data fra server og oppdatere lokallager med disse. */
    async oppdaterTilgjengeligeGrupper(): Promise<void> {
        await firstValueFrom(this.tilgjengeligeGrupperMedMeta().pipe(kunRequestRespons()));
    }

    gruppemaler(): Observable<EnkelGruppeModell[]> {
        return this.gruppeRessurs.finnMalgrupper().somObservable();
    }

    opprett(spesifikasjon: NyGruppeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.opprettGruppeMedInnloggetBruker(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const nyGruppe = respons.body!;
                  this.gruppeLager.update(upsertEntities(nyGruppe));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke opprette gruppe')), 'opprett')
        }));
    }

    opprettMedNyAvtale(spesifikasjon: NyAvtaleOgGruppeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.opprettAvtaleOgGruppeMedInnloggetBruker(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const nyGruppe = respons.body!;
                  this.gruppeLager.update(upsertEntities(nyGruppe));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke opprette gruppe')), 'opprettMedNyAvtale')
        }));
    }

    oppdater(id: GruppeId, spesifikasjon: OppdaterGruppeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertGruppe = respons.body!;
                  this.gruppeLager.update(updateEntities(lesId(oppdatertGruppe, this.gruppeLager), oppdatertGruppe));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere gruppe')), 'oppdater')
        }));
    }

    oppdaterMøtevurderingAktivert(id: GruppeId, aktivert: boolean, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.oppdaterMøtevurderingAktivert(id, {aktivert}).råRespons()
              .pipe(tap(respons => {
                  const oppdatertGruppe = respons.body!;
                  this.gruppeLager.update(updateEntities(lesId(oppdatertGruppe, this.gruppeLager), oppdatertGruppe));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere gruppe')), 'oppdaterMøtevurderingAktivert')
        }));
    }

    oppdaterProtokollValg(id: GruppeId, spesifikasjon: ProtokollValgSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta) {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.oppdaterProtokollValg(id, spesifikasjon).råRespons()
                .pipe(tap(respons => {
                    const protokollValg = respons.body!;
                    this.gruppeLager.update(updateEntities(id, gruppe =>
                        ({...gruppe, oppsett: {...gruppe.oppsett, protokollValg}} as GruppeModell)));
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere protokolloppsett')), 'oppdaterProtokollValg')
        }));
    }

    /** Oppdaterer {@link OppsettModell#aksepterteSignaturmetoder} for angitt gruppe i lokallager dersom lageret inneholder gruppen. */
    oppdaterStandardSignaturmetoderILokallager(id: GruppeId, signaturmetoder: AkseptertSignaturmetode[]): void {
        this.gruppeLager.update(updateEntities(id, gruppe =>
          ({...gruppe, oppsett: {...gruppe.oppsett, aksepterteSignaturmetoder: signaturmetoder}} as GruppeModell)));
    }

    async oppdaterAgendadokumentValg(id: string, spesifikasjon: AgendaDokumentValgSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta):Promise<void> {
        const oppdaterteAgendadokumentValg = await  this.gruppeRessurs.oppdaterAgendadokumentValg(id, spesifikasjon)
          .somPromiseVisMeldingVedFeil(i18n('Kunne ikke oppdatere agendadokument-valg'))
        this.gruppeLager.update(updateEntities(id, gruppe =>
          ({...gruppe, oppsett: {...gruppe.oppsett, agendaDokumentValg: oppdaterteAgendadokumentValg}} as GruppeModell)));
    }

    async oppdaterForfalteOppgaverDokumentValg(id: GruppeId, spesifikasjon: ForfalteOppgaverDokumentValgSpesifikasjon): Promise<void> {
        const oppdaterteValg = await this.gruppeRessurs.oppdaterForfalteOppgaverDokumentValg(id, spesifikasjon)
            .somPromiseVisMeldingVedFeil(i18n('Kunne ikke oppdatere oppgavedokument-valg'));
        this.gruppeLager.update(updateEntities(id, gruppe =>
            ({...gruppe, oppsett: {...gruppe.oppsett, forfalteOppgaverDokumentValg: oppdaterteValg}} as GruppeModell)));
    }

    async slett(id: GruppeId, spesifikasjon: SlettGruppeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SlettGruppeResponsModell> {
        return await firstValueFrom(serverFraRequest({
            ressurskall: () => this.gruppeRessurs.slett(id, spesifikasjon).råRespons()
              .pipe(
                tap(respons => {
                    const responseModell = respons.body!;
                    if (responseModell.slettet)
                        this.gruppeLager.update(deleteEntities(responseModell.gruppeId));
                }),
              ),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette gruppe')), 'slettGruppe')
        }));
    }

    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i gruppe må lager som håndterer data inkludert i gruppe oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.gruppeLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer gruppeLager ${hendelse.type} til GruppemedlemDatatjeneste for gruppene ${hendelse.ids}`, hendelse, entiteter);
            entiteter.forEach(gruppe => this.gruppemedlemDatatjeneste.erstattMedlemmerILokallager(gruppe.id, gruppe.alleMedlemmer));
        });

        slettHendelse(this.gruppeLager).subscribe(hendelse => {
            debug(`propagerer gruppeLager ${hendelse.type} til GruppemedlemDatatjeneste for gruppene ${hendelse.ids}`);
            this.gruppemedlemDatatjeneste.slettMedlemmerTilknyttetGrupperILokallager(hendelse.ids);
        });
    }
}

const standardSortering = sorterGrupperPåOrganisasjonSåTittel;


/** Datalager-spesifikk representasjon av en GruppeModell der medlem-lister er fjernet.
 * Dette da datalageret ikke garanterer at disse listen er oppdatert.
 * Medlemmer må hentes via {@link GruppemedlemDatatjeneste} */
export type GruppeModellDML = Omit<GruppeModell, 'alleMedlemmer' | 'medlemmer' | 'inaktiveMedlemmer' | 'invitasjoner'>;
